import { useEffect, useState } from "react";
import { Check, X, Send, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/common";
import { api, errMsg, fmtDateTime } from "@/lib/api";

export function RegistrationsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/registrations").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const decide = async (u, approve) => {
    try {
      await api.post(`/admin/registrations/${u.id}`, { approve });
      toast.success(approve ? `${u.name} approvato` : "Registrazione rifiutata");
      load();
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items.length) return null;
  return (
    <Panel title={`Richieste di registrazione (${items.length})`} className="mb-6 border-amber-300 dark:border-amber-500/40" testId="registrations-panel">
      <ul className="divide-y">
        {items.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm" data-testid={`registration-row-${u.id}`}>
            <span className="min-w-0 flex-1">
              <span className="font-medium">{u.name}</span>
              <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{u.role === "admin" ? "Amministratore" : "Dipendente"}</span>
              <span className="block text-xs text-muted-foreground">{u.email} · email verificata · {fmtDateTime(u.created_at)}</span>
            </span>
            <Button size="sm" onClick={() => decide(u, true)} data-testid={`registration-approve-${u.id}`}><Check className="mr-1 h-4 w-4" />Approva</Button>
            <Button size="sm" variant="outline" onClick={() => decide(u, false)} data-testid={`registration-reject-${u.id}`}><X className="mr-1 h-4 w-4" />Rifiuta</Button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function RegLinksButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const emails = text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Inserisci almeno un'email");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/registration-links", { emails });
      if (data.sent.length) toast.success(`Link inviato a ${data.sent.length} ${data.sent.length === 1 ? "persona" : "persone"}`);
      if (data.skipped.length) toast.warning(`Non inviati (già registrati o non validi): ${data.skipped.join(", ")}`);
      setOpen(false);
      setText("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="send-registration-links-button"><UserPlus className="mr-1.5 h-4 w-4" />Invia link di registrazione</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="registration-links-dialog">
          <DialogHeader>
            <DialogTitle>Invia link di registrazione</DialogTitle>
            <DialogDescription>Ogni persona riceve un link personale (valido 7 giorni) per creare da sola il proprio profilo da dipendente, senza bisogno di approvazione.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Email (una per riga o separate da virgola)</Label>
            <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={"mario.verdi@azienda.it\nlucia.neri@azienda.it"} data-testid="registration-links-input" />
          </div>
          <DialogFooter><Button onClick={send} disabled={busy} data-testid="registration-links-submit"><Send className="mr-1.5 h-4 w-4" />Invia</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
